/**
 * Entity Grid Component
 * Reusable grid for one-to-many sub-entities
 * Used for: Instructions, DataSamples, Templates, Quotas, Capabilities, etc.
 */

import React from 'react';
import { useUIStore } from '@flow-studio/store';
import './EntityGrid.css';
import { TrashIcon } from '@bizfirst/common-react';
import { Loader } from './Loader';

export interface ColumnDefinition {
  key: string;
  label: string;
  width?: string;
  render?: (value: any, entity: any) => React.ReactNode;
}

export interface EntityGridProps<T extends { [key: string]: any }> {
  title: string;
  entities: T[];
  columns: ColumnDefinition[];
  onAdd: () => void;
  onEdit: (entity: T) => void;
  onDelete: (id: number | string) => void;
  loading: boolean;
  emptyMessage?: string;
  gridColumns?: number; // 1, 2, or 3 columns
  idField?: string; // Field name for entity ID (default: 'ID' or first key)
}

export const EntityGrid = React.forwardRef<HTMLDivElement, EntityGridProps<any>>(
  (
    {
      title,
      entities,
      columns,
      onAdd,
      onEdit,
      onDelete,
      loading,
      emptyMessage = 'No items yet. Click "Add" to create one.',
      gridColumns = 2,
      idField,
    },
    ref
  ) => {
    const { showToast } = useUIStore();

    // Determine ID field
    const getEntityId = (entity: any): number | string => {
      if (idField) return entity[idField];
      // Try common ID field names
      const commonIdFields = ['ID', 'id', 'EntityID', 'EntityId'];
      for (const field of commonIdFields) {
        if (field in entity) return entity[field];
      }
      // Fallback to first key
      return Object.keys(entity)[0];
    };

    return (
      <div ref={ref} className="entity-grid-container">
        <div className="entity-grid-header">
          <h3 className="entity-grid-title">{title}</h3>
          <button
            className="entity-grid-add-btn"
            onClick={onAdd}
            disabled={loading}
            title="Add new item"
          >
             Add
          </button>
        </div>

        {loading && (
          <div className="entity-grid-loading">
            <Loader label="Loading..." />
          </div>
        )}

        {!loading && entities.length === 0 && (
          <div className="entity-grid-empty">{emptyMessage}</div>
        )}

        {!loading && entities.length > 0 && (
          <div
            className="entity-grid-wrapper"
            style={{
              gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
            }}
          >
            {entities.map((entity) => {
              const entityId = getEntityId(entity);
              return (
                <div key={entityId} className="entity-grid-card">
                  <div className="entity-card-content">
                    {columns.map((column) => (
                      <div key={column.key} className="entity-card-field">
                        <label className="entity-card-label">
                          {column.label}
                        </label>
                        <div className="entity-card-value">
                          {column.render
                            ? column.render(entity[column.key], entity)
                            : entity[column.key] ?? '—'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="entity-card-actions">
                    <button
                      className="entity-card-edit-btn"
                      onClick={() => onEdit(entity)}
                      title="Edit"
                    >
                       
                    </button>
                    <button
                      className="entity-card-delete-btn"
                      onClick={() => {
                        onDelete(entityId);
                        showToast('success', 'Item deleted');
                      }}
                      title="Delete"
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

EntityGrid.displayName = 'EntityGrid';

export default EntityGrid;
